<!--
    Params
      - type  - frequency key
      - value - seconds (optional)
      - ConfirmCallback can optionally return promise. In that case the callback will have the then block, and will wait for the success to close the modal.
-->

<template>
  <ValidationObserver
    ref="flowFrequencyObserver"
    v-slot="{ valid }" >
    <b-modal
      ref="flowFrequencyModal"
      modal-class="frequency-modal"
      :size="size"
      :title="title"
      centered
      @hidden="reset"
    >
      <template slot="modal-header">
        <h2>{{ title }}</h2>
      </template>
      <div>
        <b-form-group>
          <label
            for=""
            class="settings__label-sub" >
            Frequency
          </label>
          <ValidationProvider
            v-slot="{ errors }"
            rules="required">
            <multiselect
              v-model="frequency"
              placeholder="Select an option"
              class="flex-options hide-tags no-selected-background"
              :options="options"
              label="text"
              track-by="value"
              :multiple="false"
              :show-labels="false"
              :show-no-results="false"
              :close-on-select="true"
              :clear-on-select="false"
              :preserve-search="true"
              :allow-empty="false"
              :preselect-first="true"
              :searchable="false" >
              <template
                slot="selection">
                <img
                  v-if="frequency"
                  :src="frequency.icon"
                  class="mr-2"
                  height="12" >
              </template>
              <template
                slot="option"
                slot-scope="props">
                <SBIcon
                  :icon="SBIconList.DONE_TICK"
                  :color="SBColors.bluePrimary"
                  :size="9"/>
                <img
                  :src="props.option.icon"
                  class="mr-2"
                  height="12" >
                {{ props.option.text }}
              </template>
            </multiselect>
            <div
              v-if="errors[0]"
              class="error flex flex-align-center">
              <SBIcon
                class="mr-2"
                :icon="SBIconList.ERROR"
                :color="SBColors.redPrimary"
                :size="12" />
              <span>Required field</span>
            </div>
          </ValidationProvider>
        </b-form-group>

        <b-form-group
          v-if="showTimeInput">
          <label
            for=""
            class="settings__label-sub" >
            Time
          </label>
          <ValidationProvider
            rules="required|validChild">
            <TimeFrequency
              v-model="time"
              @keyup.native.enter="confirm" />
          </ValidationProvider>
        </b-form-group>
      </div>
      <template slot="modal-footer">
        <div class="modal-footer__buttons flex flex-row flex-nowrap">
          <b-button
            :disabled="working"
            @click="hide" >{{ cancelButtonText }}</b-button>
          <b-button
            v-if="onConfirmCallback"
            class=""
            :disabled="!valid || working"
            :variant="buttonVariant"
            :class="{loading: working}"
            @click="confirm" >
            <span>{{ confirmButtonText }}</span>
            <Spinner
              v-if="working"
              class="absolute"
              :size="14"
              :thickness="2"
              :color="SBColors.white" />
          </b-button>
        </div>
      </template>
    </b-modal>
  </ValidationObserver>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';

import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import SBColors from '@/helpers/lists/colors';
import Spinner from '@/components/common/Spinner';
import TimeFrequency from '@/components/common/TimeFrequency';

export default {
  name: 'FlowFrequencyModalComponent',
  components: {
    SBIcon,
    Spinner,
    Multiselect,
    TimeFrequency,
  },
  data() {
    return {
      editMode: false,
      validationErrors: {},
      title: 'Edit trigger frequency',
      defaultSize: 'sm',
      confirmButtonText: 'Save',
      variant: 'info',
      size: '',
      buttonVariant: 'primary',
      cancelButtonText: 'Cancel',
      working: false,
      onConfirmCallback: null,
      frequency: null,
      time: {
        seconds: 300,
      },
      SBIconList,
      SBColors,
    };
  },
  computed: {
    ...mapGetters({
      flowsDefinitionsUI: 'flows/flowsDefinitionsUI',
    }),
    options() {
      const opts = [];
      Object.values(this.flowsDefinitionsUI.trigger_frequency).forEach((frequency) => {
        const opt = {};
        opt.value = frequency.type;
        opt.description = frequency.description_template || frequency.description;
        if (frequency.description_template) {
          opt.text = frequency.description_template.replace(' {{TIME_PLACEHOLDER}}', ':');
        } else {
          opt.text = frequency.description;
        }
        opt.icon = frequency.icon;
        opts.push(opt);
      });
      return opts;
    },
    showTimeInput() {
      return this.frequency && this.frequency.description && this.frequency.description.indexOf('{{TIME_PLACEHOLDER}}') >= 0;
    },
  },
  beforeMount() {
    this.$eventBus.$on('showFlowFrequencyModal', (params) => {
      this.show(params);
    });
  },
  methods: {
    ...mapActions({}),
    show(params) {
      this.working = false;
      if (typeof params.confirmCallback === 'function') {
        this.onConfirmCallback = params.confirmCallback;
      }
      this.frequency = this.options.find(o => o.value === params.type);
      if (params.value) this.time.seconds = params.value;
      this.$refs.flowFrequencyModal.show();
      setTimeout(() => {
        this.$refs.flowFrequencyObserver.validate();
      }, 0);
    },
    hide() {
      this.$refs.flowFrequencyModal.hide();
    },
    confirm() {
      this.$refs.flowFrequencyObserver.validate()
        .then((valid) => {
          if (valid) {
            this.working = true;
            const confirmData = {
              frequency: this.frequency,
            };
            if (this.showTimeInput) {
              confirmData.time = this.time;
            }
            const confirm = this.onConfirmCallback(confirmData);
            if (this.isPromise(confirm)) {
              confirm
                .then(() => {
                  this.hide();
                })
                .catch(() => {
                  this.working = false;
                });
            }
          }
        });
    },
    reset() {
      this.onConfirmCallback = null;
      this.size = '';
      this.working = false;
      this.$refs.flowFrequencyObserver.reset();
    },
  },
};
</script>

<style lang="scss">
.frequency-modal {
  .modal-content {
    .modal-header {
      padding: 20px;
      box-shadow: none;
      border-bottom: solid 1px $greyBorderModal;
      display: flex;
      flex-direction: column;

      h2 {
        font-size: 16px;
        font-weight: 500 !important;
        color: $blackPrimary;
        margin-bottom: 4px;
      }

      p {
        font-size: 14px;
        color: $blackUltralight;
      }
    }
    .modal-body {
      padding: 20px;
      font-size: 14px;
      font-weight: normal;
      line-height: 1.2;
      color: $blackPrimary;
      background-color: $greyUltralight;
    }
    .modal-footer {
      border-top: solid 1px $greyBorderModal;
      align-items: center;
      flex-direction: row;
    }
    .modal-footer>* {
      margin: 0;
    }
    .modal-footer__message {
      // style if needed
    }
    .modal-footer__buttons {
      // style if needed
      .btn.loading {
        position: relative;

        span {
          visibility: hidden;
        }

        .loader {
          position: absolute;
          height: 100%;
          display: flex;
          align-items: center;
        }
      }
    }
    .modal-footer__undo-warning {
      color: $redPrimary;
      font-size: 14px;
      font-weight: 400;
      // icon 4px from text
      .sb2-icon {
        margin-right: 4px;
      }
    }
  }
}
</style>
